// Registry loader + block-state interning (ARCH §3.1..3.3; BLK-2/BLK-5).
//
// PURE (ENG-1): callers read the JSON (fs on the server/tests, import.meta.glob in
// the browser) and hand the parsed objects here. This module validates and interns;
// it never touches the filesystem or DOM.
import { z } from 'zod';
import { validateGenes } from '../heredity/genes';
import { validateLesson, type LessonRegRefs } from '../education/lessons';
import {
  blockSchema,
  itemSchema,
  lootSchema,
  recipeSchema,
  soundGroupSchema,
  enchantmentSchema,
  whitelistSchema,
  biomeSchema,
  placementRuleSchema,
  splineSchema,
  featuresSchema,
  mobSchema,
  tradeTableSchema,
  raidTableSchema,
  lessonSchema,
  resolvePropertyValues,
  type BlockDef,
  type ItemDef,
  type LootTable,
  type RecipeDef,
  type SoundGroup,
  type EnchantmentDef,
  type Whitelist,
  type Biome,
  type PlacementRule,
  type TerrainSpline,
  type Features,
  type MobDef,
  type TradeTable,
  type RaidTable,
  type LessonDef,
} from './schemas';

/** Parsed-but-unvalidated JSON, grouped by registry (read by an out-of-sim adapter). */
export interface RawData {
  blocks: unknown[];
  items: unknown[];
  loot: unknown[];
  recipes?: unknown[];
  sounds: unknown[];
  enchantments?: unknown[];
  whitelist?: unknown;
  biomes?: unknown[];
  placement?: unknown[];
  spline?: unknown;
  features?: unknown;
  mobs?: unknown[];
  trades?: unknown[];
  raids?: unknown[];
  lessons?: unknown[];
}

export interface BiomeRegistry {
  defs: readonly Biome[];
  byName: ReadonlyMap<string, Biome>;
  placement: readonly PlacementRule[];
  spline: TerrainSpline;
}

/** Global block-state table (BLK-2). One entry per interned state; index = u16 id. */
export interface StateTable {
  readonly count: number;
  /** stateId -> owning block name. */
  readonly blockName: readonly string[];
  /** stateId -> resolved property values ({} for propertyless blocks). */
  readonly props: readonly Readonly<Record<string, string>>[];
  /** stateId -> canonical key, e.g. `oak_log[axis=y]` or `stone`. */
  readonly key: readonly string[];
  /** block name -> its default (first-enumerated) stateId. */
  readonly defaultOf: ReadonlyMap<string, number>;
  /** block name -> all of its stateIds, in enumeration order. */
  readonly statesOf: ReadonlyMap<string, readonly number[]>;
  /** canonical key -> stateId. */
  readonly idByKey: ReadonlyMap<string, number>;
}

export interface Registries {
  readonly blocks: {
    readonly byName: ReadonlyMap<string, BlockDef>;
    readonly list: readonly BlockDef[];
  };
  readonly items: {
    readonly byName: ReadonlyMap<string, ItemDef>;
    readonly list: readonly ItemDef[];
    /** item name -> u16 item id (id 0 is the reserved EMPTY sentinel). */
    readonly idOf: ReadonlyMap<string, number>;
    readonly nameOf: readonly string[];
  };
  readonly loot: ReadonlyMap<string, LootTable>;
  readonly recipes: {
    readonly list: readonly RecipeDef[];
    readonly byName: ReadonlyMap<string, RecipeDef>;
  };
  readonly sounds: ReadonlyMap<string, SoundGroup>;
  /** Enchantments (PLR-6; M12.1), keyed by id. */
  readonly enchantments: {
    readonly byId: ReadonlyMap<string, EnchantmentDef>;
    readonly list: readonly EnchantmentDef[];
  };
  readonly states: StateTable;
  /** Biome/terrain data (GEN-3..5); null when a minimal registry omits it. */
  readonly biomes: BiomeRegistry | null;
  /** Ore/feature config (GEN-7); null when omitted. */
  readonly features: Features | null;
  /** Mob definitions (MOB-1..5), keyed by name. Empty when a registry omits them. */
  readonly mobs: {
    readonly byName: ReadonlyMap<string, MobDef>;
    readonly list: readonly MobDef[];
  };
  /** Villager trade tables (VIL; M14.1), keyed by name. */
  readonly trades: ReadonlyMap<string, TradeTable>;
  /** Raid wave tables (RAID; M14.2), keyed by name. */
  readonly raids: ReadonlyMap<string, RaidTable>;
  /** Lesson packs (EDU-1, ARCH §29.2), keyed by `name` (D-TF-188). Empty when a
   *  registry omits them (every M51-and-earlier registry, and any without a
   *  loaded `lessons` collection). */
  readonly lessons: {
    readonly byName: ReadonlyMap<string, LessonDef>;
    readonly list: readonly LessonDef[];
  };
}

/** ItemStack item id reserved for an empty slot (PLR-1). */
export const ITEM_EMPTY = 0;
/** BLK-2: `air` is always state 0. */
export const STATE_AIR = 0;
const MAX_STATES = 65_536; // u16 ceiling.

function validateAll<S extends z.ZodType>(schema: S, raw: unknown[], kind: string): z.output<S>[] {
  return raw.map((r, i) => {
    const res = schema.safeParse(r);
    if (!res.success) {
      throw new Error(`${kind}[${i}] failed validation: ${res.error.issues.map((e) => `${e.path.join('.')} ${e.message}`).join('; ')}`);
    }
    return res.data;
  });
}

/** Canonical `name[k=v,...]` key in declaration order. */
function makeKey(name: string, props: Record<string, string>): string {
  const keys = Object.keys(props);
  if (keys.length === 0) return name;
  return `${name}[${keys.map((k) => `${k}=${props[k]}`).join(',')}]`;
}

/**
 * Enumerate a block's states as a mixed-radix counter with the FIRST-declared
 * property most-significant (BLK-2). Returns props objects in enumeration order.
 */
function enumerateStates(block: BlockDef): Array<Record<string, string>> {
  const dims = resolvePropertyValues(block.name, block.properties);
  let states: Array<Record<string, string>> = [{}];
  for (const [propName, values] of dims) {
    const next: Array<Record<string, string>> = [];
    for (const partial of states) {
      for (const v of values) next.push({ ...partial, [propName]: v });
    }
    states = next;
  }
  return states;
}

export function loadRegistries(raw: RawData): Registries {
  const blocks = validateAll(blockSchema, raw.blocks, 'block');
  const items = validateAll(itemSchema, raw.items, 'item');
  const loots = validateAll(lootSchema, raw.loot, 'loot');
  const recipeDefs = validateAll(recipeSchema, raw.recipes ?? [], 'recipe');
  const sounds = validateAll(soundGroupSchema, raw.sounds, 'sound');

  // ── Deterministic block order: air first (state 0), then by name (BLK-2). ──
  const air = blocks.find((b) => b.name === 'air');
  if (!air) throw new Error("registry requires an 'air' block (state 0, BLK-2)");
  const ordered = [air, ...blocks.filter((b) => b.name !== 'air').sort((a, b) => a.name.localeCompare(b.name))];

  const blockName: string[] = [];
  const props: Record<string, string>[] = [];
  const key: string[] = [];
  const defaultOf = new Map<string, number>();
  const statesOf = new Map<string, number[]>();
  const idByKey = new Map<string, number>();

  for (const block of ordered) {
    if (statesOf.has(block.name)) throw new Error(`duplicate block '${block.name}'`);
    const enumerated = enumerateStates(block);
    const ids: number[] = [];
    for (const p of enumerated) {
      const id = blockName.length;
      if (id >= MAX_STATES) {
        throw new Error(`block-state count exceeded u16 ceiling (${MAX_STATES}) at '${block.name}' (BLK-2)`);
      }
      const k = makeKey(block.name, p);
      blockName.push(block.name);
      props.push(p);
      key.push(k);
      if (idByKey.has(k)) throw new Error(`duplicate state key '${k}'`);
      idByKey.set(k, id);
      ids.push(id);
    }
    defaultOf.set(block.name, ids[0]!);
    statesOf.set(block.name, ids);
  }

  if (blockName[STATE_AIR] !== 'air') throw new Error('invariant: state 0 is not air (BLK-2)');

  // ── Items: id 0 = EMPTY sentinel, real items sorted by name → 1..N. ──
  const itemsSorted = [...items].sort((a, b) => a.name.localeCompare(b.name));
  const itemByName = new Map<string, ItemDef>();
  const idOf = new Map<string, number>([['empty', ITEM_EMPTY]]);
  const nameOf: string[] = ['empty'];
  for (const it of itemsSorted) {
    if (itemByName.has(it.name)) throw new Error(`duplicate item '${it.name}'`);
    itemByName.set(it.name, it);
    idOf.set(it.name, nameOf.length);
    nameOf.push(it.name);
  }

  const lootByName = new Map<string, LootTable>();
  for (const t of loots) {
    if (lootByName.has(t.name)) throw new Error(`duplicate loot table '${t.name}'`);
    lootByName.set(t.name, t);
  }
  const recipeByName = new Map<string, RecipeDef>();
  for (const r of recipeDefs) {
    if (recipeByName.has(r.name)) throw new Error(`duplicate recipe '${r.name}'`);
    recipeByName.set(r.name, r);
  }
  const soundByName = new Map<string, SoundGroup>();
  for (const s of sounds) {
    if (soundByName.has(s.name)) throw new Error(`duplicate sound group '${s.name}'`);
    soundByName.set(s.name, s);
  }

  let biomes: BiomeRegistry | null = null;
  if (raw.biomes && raw.biomes.length) {
    const defs = validateAll(biomeSchema, raw.biomes, 'biome');
    const placement = validateAll(placementRuleSchema, raw.placement ?? [], 'placement');
    const spline = splineSchema.parse(raw.spline);
    biomes = { defs, byName: new Map(defs.map((b) => [b.name, b])), placement, spline };
  }

  const features: Features | null = raw.features ? featuresSchema.parse(raw.features) : null;

  const enchantDefs = validateAll(enchantmentSchema, raw.enchantments ?? [], 'enchantment');
  const enchantById = new Map<string, EnchantmentDef>();
  for (const e of enchantDefs) {
    if (enchantById.has(e.id)) throw new Error(`duplicate enchantment '${e.id}'`);
    enchantById.set(e.id, e);
  }

  const mobDefs = validateAll(mobSchema, raw.mobs ?? [], 'mob');
  const mobByName = new Map<string, MobDef>();
  for (const m of mobDefs) {
    if (mobByName.has(m.name)) throw new Error(`duplicate mob '${m.name}'`);
    // §28.3: a gene table's cross-field rules are a LOAD error, never a genome that
    // decodes wrong mid-world.
    if (m.genes) {
      const bad = validateGenes(m.genes, m.rig.map((part) => part.name));
      if (bad) throw new Error(`mob '${m.name}': ${bad}`);
    }
    mobByName.set(m.name, m);
  }

  const tradeDefs = validateAll(tradeTableSchema, raw.trades ?? [], 'trade');
  const tradeByName = new Map<string, TradeTable>();
  for (const t of tradeDefs) {
    if (tradeByName.has(t.name)) throw new Error(`duplicate trade table '${t.name}'`);
    tradeByName.set(t.name, t);
  }

  const raidDefs = validateAll(raidTableSchema, raw.raids ?? [], 'raid');
  const raidByName = new Map<string, RaidTable>();
  for (const t of raidDefs) {
    if (raidByName.has(t.name)) throw new Error(`duplicate raid table '${t.name}'`);
    raidByName.set(t.name, t);
  }

  const blockByName = new Map(ordered.map((b) => [b.name, b]));
  const lessonDefs = validateAll(lessonSchema, raw.lessons ?? [], 'lesson');
  const lessonByNameMap = new Map<string, LessonDef>();
  const lessonRefs: LessonRegRefs = { mobs: mobByName, blocks: blockByName, items: itemByName };
  for (const l of lessonDefs) {
    if (lessonByNameMap.has(l.name)) throw new Error(`duplicate lesson '${l.name}'`);
    // §29.2's cross-field rules — never a load error until they can be checked
    // against the merged registry, exactly as `validateGenes` runs after `mobSchema`.
    const bad = validateLesson(l, lessonRefs);
    if (bad) throw new Error(`lesson '${l.name}': ${bad}`);
    lessonByNameMap.set(l.name, l);
  }

  const registries: Registries = {
    blocks: { byName: blockByName, list: ordered },
    items: { byName: itemByName, list: itemsSorted, idOf, nameOf },
    loot: lootByName,
    recipes: { list: recipeDefs, byName: recipeByName },
    sounds: soundByName,
    enchantments: { byId: enchantById, list: enchantDefs },
    biomes,
    features,
    mobs: { byName: mobByName, list: mobDefs },
    trades: tradeByName,
    raids: raidByName,
    lessons: { byName: lessonByNameMap, list: lessonDefs },
    states: {
      count: blockName.length,
      blockName,
      props,
      key,
      defaultOf,
      statesOf,
      idByKey,
    },
  };
  return Object.freeze(registries);
}

/** All item names a loot table can possibly yield (used by the audit). */
export function lootTableItems(table: LootTable): Set<string> {
  const out = new Set<string>();
  for (const pool of table.pools) {
    for (const entry of pool.entries) {
      if (entry.type === 'item' && entry.name) out.add(entry.name);
    }
  }
  return out;
}

export interface ObtainabilityResult {
  ok: boolean;
  orphans: string[];
}

/**
 * BLK-5: every registered item must be obtainable — dropped by a block (dropSelf
 * or a loot table), produced by a recipe, dropped by a mob, or explicitly listed
 * creative-only. Block drops joined at M0.2; recipe outputs join here at M4.2;
 * mob drops extend the set at M5.
 */
export function checkObtainability(reg: Registries, whitelist: Whitelist): ObtainabilityResult {
  const obtainable = new Set<string>();

  for (const block of reg.blocks.list) {
    if (block.dropSelf && reg.items.byName.has(block.name)) obtainable.add(block.name);
  }
  for (const table of reg.loot.values()) {
    for (const item of lootTableItems(table)) obtainable.add(item);
  }
  for (const recipe of reg.recipes.list) obtainable.add(recipe.result.item);
  // A villager trade is a way to obtain its sell item (VIL, M14.1).
  for (const table of reg.trades.values()) for (const offer of table.offers) obtainable.add(offer.sell.item);

  const creativeOnly = new Set(whitelist.items);
  const orphans = reg.items.list
    .map((i) => i.name)
    .filter((name) => !obtainable.has(name) && !creativeOnly.has(name));

  return { ok: orphans.length === 0, orphans };
}

export function parseWhitelist(raw: unknown): Whitelist {
  return whitelistSchema.parse(raw ?? { items: [] });
}
